"use client";
import { useEffect, useState } from "react";

interface CoinData {
  symbol: string;
  name: string;
  price: string;
  change: string;
  up: boolean;
}

const COIN_IDS = "bitcoin,ethereum,solana,ripple,chainlink,avalanche-2";

function formatPrice(price: number): string {
  if (price >= 1000) return `$${price.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  if (price >= 1) return `$${price.toFixed(2)}`;
  return `$${price.toFixed(4)}`;
}

function useLiveMarket() {
  const [markets, setMarkets] = useState<CoinData[]>([]);

  useEffect(() => {
    async function fetchPrices() {
      try {
        const res = await fetch(
          `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${COIN_IDS}&order=market_cap_desc&sparkline=false&price_change_percentage=24h`
        );
        const data = await res.json();
        const mapped: CoinData[] = data.map((coin: { symbol: string; name: string; current_price: number; price_change_percentage_24h: number }) => ({
          symbol: coin.symbol.toUpperCase(),
          name: coin.name,
          price: formatPrice(coin.current_price),
          change: `${coin.price_change_percentage_24h >= 0 ? "+" : ""}${coin.price_change_percentage_24h.toFixed(1)}%`,
          up: coin.price_change_percentage_24h >= 0,
        }));
        setMarkets(mapped);
      } catch {
        // Silently fail — ticker just won't show until next refresh
      }
    }
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, []);

  return markets;
}

export function MarketTicker() {
  const markets = useLiveMarket();

  if (markets.length === 0) return null;

  return (
    <section className="border-y border-white/5 bg-panel/50 overflow-hidden py-4">
      <div className="animate-ticker flex gap-8 whitespace-nowrap">
        {[...markets, ...markets].map((m, i) => (
          <div key={i} className="inline-flex items-center gap-3 px-4">
            <span className="text-white font-semibold text-sm">{m.symbol}</span>
            <span className="text-silver text-sm font-mono">{m.price}</span>
            <span className={`text-sm font-mono ${m.up ? "text-success" : "text-danger"}`}>{m.change}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function MarketSection() {
  const markets = useLiveMarket();

  return (
    <section id="market" className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Live Market Overview</h2>
          <p className="text-silver max-w-xl mx-auto">Real-time market data powering our trading systems.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {markets.map((m) => (
            <div key={m.symbol} className="bg-panel border border-white/5 rounded-xl p-5 hover:border-gold/30 transition group">
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-bold">{m.symbol}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${m.up ? "bg-success/10 text-success" : "bg-danger/10 text-danger"}`}>
                  {m.change}
                </span>
              </div>
              <div className="text-xl font-mono font-semibold mb-1">{m.price}</div>
              <div className="text-xs text-silver">{m.name}</div>
              {/* Mini chart */}
              <div className="mt-3 h-8 flex items-end gap-0.5">
                {[40, 65, 35, 80, 55, 70, 45, 90, 60, 75, 50, 85].map((h, j) => (
                  <div
                    key={j}
                    className={`flex-1 rounded-sm ${m.up ? "bg-success/40" : "bg-danger/40"} transition`}
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
